import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Info,
  Clock,
  Download,
  Loader2,
  X
} from 'lucide-react';
import BookingProgress from '../components/ui/BookingProgress';
import { repeatCarBooking } from '../utils/bookingProgress';
import RenterLayout from '../components/layout/RenterLayout';
import { renterService } from '../services/renter.service';
import { downloadBookingInvoice } from '../utils/bookingInvoice';
import { buildPublicFileUrl } from '../utils/fileUrl';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=800';

const getBookingImage = (imageUrl) => {
  if (!imageUrl) return FALLBACK_IMAGE;
  return buildPublicFileUrl(imageUrl);
};

const formatCurrency = (amount) => `RF ${Math.round(amount || 0).toLocaleString()}`;
const formatPaymentStatus = (status) => (status || 'UNKNOWN').replace(/_/g, ' ');

const getBadgeClasses = (status) => {
  switch (status) {
    case 'Ongoing':
      return 'bg-green-50 border-green-500 text-green-700';
    case 'Confirmed':
      return 'bg-brand-orange-50 border-brand-orange-500 text-brand-black';
    case 'Pending Payment':
      return 'bg-amber-50 border-amber-500 text-amber-700';
    case 'Completed':
      return 'bg-gray-50 border-gray-400 text-gray-600';
    case 'Cancelled':
    case 'Cancelled By Owner':
      return 'bg-red-50 border-red-500 text-red-700';
    case 'Refunded':
      return 'bg-brand-orange-50 border-brand-orange-500 text-brand-black';
    default:
      return 'bg-gray-50 border-gray-300 text-gray-500';
  }
};

const MyBookings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('current');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await renterService.getMyBookings();
      setBookings(response);
    } catch (err) {
      setError(err.message || 'Failed to load your bookings.');
    } finally {
      setLoading(false);
    }
  };

  const { currentBookings, pastBookings } = useMemo(() => {
    const mappedBookings = bookings.map((booking) => ({
      id: booking.id,
      car: {
        id: booking.carId,
        name: booking.carName,
        year: booking.carYear,
        image: getBookingImage(booking.carImageUrl)
      },
      carDisplayName: [booking.carYear, booking.carName].filter(Boolean).join(' '),
      rawStatus: booking.status,
      status: booking.statusLabel,
      paymentStatus: booking.paymentStatus,
      paymentStatusLabel: formatPaymentStatus(booking.paymentStatus),
      paymentMethod: booking.paymentMethod,
      paymentMethodLabel: booking.paymentMethod ? booking.paymentMethod.replace(/_/g, ' ') : 'Pending Payment',
      transactionRef: booking.transactionRef,
      paidAt: booking.paidAt,
      createdAt: booking.createdAt,
      startDate: booking.startDate,
      endDate: booking.endDate,
      pickupDateValue: booking.pickupDateValue,
      returnDateValue: booking.returnDateValue,
      totalDays: booking.totalDays,
      destinationLocation: booking.destinationLocation,
      pickup: booking.pickupLocation,
      pickupCityId: booking.pickupCityId,
      outsideCity: booking.outsideCity,
      price: booking.totalPrice,
      paymentDeadlineAt: booking.paymentDeadlineAt,
      cancellationReason: booking.cancellationReason,
      priceItems: booking.priceItems || [],
      customer: {
        firstName: localStorage.getItem('firstName') || '',
        lastName: localStorage.getItem('lastName') || '',
        email: localStorage.getItem('email') || '',
        phone: localStorage.getItem('phone') || ''
      },
      canPayNow: Boolean(booking.canPayNow ?? booking.payNow),
      canModify: Boolean(booking.canModify ?? booking.modify),
      canCancel: Boolean(booking.canCancel ?? booking.cancel),
      canDownloadInvoice: (booking.paymentStatus || '') === 'SUCCESS',
      reference: booking.id?.slice(0, 8).toUpperCase(),
      current: booking.current
    }));

    return {
      currentBookings: mappedBookings.filter((booking) => booking.current),
      pastBookings: mappedBookings.filter((booking) => !booking.current)
    };
  }, [bookings]);

  const displayedBookings = activeTab === 'current' ? currentBookings : pastBookings;

  const handlePayNow = async (bookingId) => {
    try {
      setActionLoadingId(bookingId);
      const response = await renterService.payBookingNow(bookingId);
      if (response?.checkoutUrl) {
        window.location.assign(response.checkoutUrl);
        return;
      }
      await loadBookings();
    } catch (err) {
      setError(err.message || 'Failed to complete booking payment.');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      setActionLoadingId(bookingId);
      await renterService.cancelBooking(bookingId);
      await loadBookings();
      if (selectedBooking?.id === bookingId) {
        setSelectedBooking(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel booking.');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleModifyReservation = (booking) => {
    navigate(`/checkout/${booking.car.id}`, {
      state: {
        bookingId: booking.id,
        bookingDetails: {
          pickupCityId: booking.pickupCityId,
          destinationLocation: booking.destinationLocation || booking.pickup,
          pickupDate: booking.pickupDateValue,
          returnDate: booking.returnDateValue,
          outsideCity: Boolean(booking.outsideCity)
        }
      }
    });
  };

  return (
    <RenterLayout
      activePage="My Bookings"
      title="My Bookings"
      description="Track every reservation, check travel dates, and stay ready for any booking updates."
    >
      <div className="flex border-b border-gray-200 mb-8 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActiveTab('current')}
          className={`pb-4 px-6 text-sm font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${
            activeTab === 'current' ? 'text-black' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Current Bookings ({currentBookings.length})
          {activeTab === 'current' && <div className="absolute bottom-0 left-0 w-full h-1 bg-black rounded-t-full"></div>}
        </button>
        <button
          onClick={() => setActiveTab('past')}
          className={`pb-4 px-6 text-sm font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${
            activeTab === 'past' ? 'text-black' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Past Bookings ({pastBookings.length})
          {activeTab === 'past' && <div className="absolute bottom-0 left-0 w-full h-1 bg-black rounded-t-full"></div>}
        </button>
      </div>

      <div className="space-y-6">
        {loading && (
          <div className="bg-white rounded-2xl p-16 shadow-sm border border-gray-100 flex justify-center">
            <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-red-100 text-center">
            <h3 className="text-xl font-black mb-2 uppercase">Unable to load bookings</h3>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-brand-black text-white px-6 py-3 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && displayedBookings.map((booking) => (
          <div key={booking.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col lg:flex-row gap-8 hover:shadow-md transition-shadow">
            <div className="lg:w-1/4 h-48 bg-gray-50 rounded-xl flex items-center justify-center p-4 border border-gray-100 relative group overflow-hidden">
              <img src={booking.car.image} alt={booking.car.name} className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute top-4 left-4">
                <span className={`text-[10px] font-black px-3 py-1.5 rounded-lg border-2 uppercase tracking-widest shadow-sm ${getBadgeClasses(booking.status)}`}>
                  {booking.status}
                </span>
              </div>
            </div>

            <div className="flex-grow flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-4 gap-4">
                  <div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                      Reference {booking.id.slice(0, 8).toUpperCase()}
                    </span>
                    <h3 className="text-2xl font-black leading-none mb-2">
                      {[booking.car.year, booking.car.name].filter(Boolean).join(' ')}
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Total Price</span>
                    <p className="text-2xl font-black">{formatCurrency(booking.price)}</p>
                  </div>
                </div>

                <BookingProgress status={booking.rawStatus} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-6 border-y border-gray-50">
                  <div className="flex items-center gap-4 text-sm font-bold text-gray-600">
                    <Calendar className="w-5 h-5 text-gray-300 shrink-0" />
                    <div>
                      <p className="text-gray-900">{booking.startDate} - {booking.endDate}</p>
                      <p className="text-[10px] uppercase font-black text-gray-400 tracking-tighter">{booking.totalDays} Days Rental</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm font-bold text-gray-600">
                    <MapPin className="w-5 h-5 text-gray-300 shrink-0" />
                    <div>
                      <p className="text-gray-900">{booking.pickup}</p>
                      <p className="text-[10px] uppercase font-black text-gray-400 tracking-tighter">Destination</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedBooking(booking)}
                    className="flex items-center gap-2 bg-brand-black text-white px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors shadow-sm"
                  >
                    <Info className="w-4 h-4" />
                    Full Details
                  </button>
                  {!booking.current && booking.car.id && <button onClick={() => navigate(`/checkout/${booking.car.id}`, { state: { bookingDetails: repeatCarBooking(booking) } })} className="bg-[#FF6B00] text-white px-5 py-2.5 rounded-lg text-xs font-bold">Book again</button>}
                  {booking.canModify && (
                    <button
                      onClick={() => handleModifyReservation(booking)}
                      className="bg-white border border-gray-200 text-gray-700 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-50 transition-colors"
                    >
                      Modify Reservation
                    </button>
                  )}
                  {booking.canPayNow && (
                    <button
                      onClick={() => handlePayNow(booking.id)}
                      disabled={actionLoadingId === booking.id}
                      className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-60"
                    >
                      {actionLoadingId === booking.id ? 'Processing...' : 'Pay Now'}
                    </button>
                  )}
                  {booking.canDownloadInvoice && (
                    <button
                      onClick={() => downloadBookingInvoice(booking)}
                      className="bg-white border border-gray-200 text-gray-700 px-6 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Invoice
                    </button>
                  )}
                </div>
                {booking.canCancel ? (
                  <button
                    onClick={() => handleCancelBooking(booking.id)}
                    disabled={actionLoadingId === booking.id}
                    className="text-red-500 text-xs font-black uppercase tracking-widest hover:text-red-700 transition-colors flex items-center gap-2 disabled:opacity-60"
                  >
                    <Clock className="w-4 h-4" />
                    Cancel Booking
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ))}

        {!loading && !error && displayedBookings.length === 0 && (
          <div className="bg-white rounded-2xl p-20 shadow-sm border border-gray-100 flex flex-col items-center text-center">
            <div className="bg-gray-50 p-6 rounded-full mb-6">
              <Calendar className="w-12 h-12 text-gray-200" />
            </div>
            <h3 className="text-xl font-black mb-2 uppercase">No {activeTab} bookings found</h3>
            <p className="text-gray-500 mb-8 max-w-sm">You don't have any bookings in this category yet. Explore our fleet and find your next ride.</p>
            <Link to="/browse" className="bg-brand-black text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-gray-800 transition-all shadow-xl">
              Browse Fleet
            </Link>
          </div>
        )}
      </div>

      {selectedBooking && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4">
          <div role="dialog" aria-modal="true" aria-label="Booking details" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-2">Booking Details</p>
                <h3 className="text-2xl font-black tracking-tight">{[selectedBooking.car.year, selectedBooking.car.name].filter(Boolean).join(' ')}</h3>
              </div>
              <button aria-label="Close booking details" onClick={() => setSelectedBooking(null)} className="rounded-full p-2 hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              <BookingProgress status={selectedBooking.rawStatus} />
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Booking Reference</p>
                  <p className="text-sm font-black text-gray-900">{selectedBooking.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Payment Status</p>
                  <p className="text-sm font-black text-gray-900">{selectedBooking.paymentStatusLabel || 'UNKNOWN'}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Trip Dates</p>
                  <p className="text-sm font-black text-gray-900">{selectedBooking.startDate} - {selectedBooking.endDate}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Destination</p>
                  <p className="text-sm font-black text-gray-900">{selectedBooking.destinationLocation || selectedBooking.pickup}</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Rental Duration</p>
                  <p className="text-sm font-black text-gray-900">{selectedBooking.totalDays} Day(s)</p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">Reservation Total</p>
                  <p className="text-sm font-black text-gray-900">{formatCurrency(selectedBooking.price)}</p>
                </div>
              </div>

              {selectedBooking.paymentDeadlineAt && selectedBooking.canPayNow && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Payment is pending. Complete payment before <span className="font-black">{selectedBooking.paymentDeadlineAt}</span> to keep this reservation.
                </div>
              )}

              {selectedBooking.cancellationReason && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {selectedBooking.cancellationReason}
                </div>
              )}

              {!!selectedBooking.priceItems?.length && (
                <div className="rounded-2xl border border-gray-100 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Invoice Breakdown</p>
                  <div className="space-y-3">
                    {selectedBooking.priceItems.map((item, index) => (
                      <div key={`${item.type || 'item'}-${index}`} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">{item.description || item.type}</span>
                        <span className="font-black text-gray-900">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedBooking.canDownloadInvoice && (
                <div className="flex justify-end">
                  <button
                    onClick={() => downloadBookingInvoice(selectedBooking)}
                    className="bg-brand-black text-white px-6 py-3 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Invoice
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </RenterLayout>
  );
};

export default MyBookings;
